import is from '@sindresorhus/is';
import type { FunctionComponent, ReactNode } from 'react';

import { useTranslate } from '~/shared/context/use-translate';
import type { JobProfileVersion } from '~/shared/data-contract/job-profile-versions';
import { Badge } from '~/ui/core/badge/Badge';
import { Button } from '~/ui/core/button/Button';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '~/ui/core/dropdown-menu/DropdownMenu';
import { IconAsterisk, IconChevronDownSmall, IconSpacer } from '~/ui/core/icons';
import { VersionSelect } from '~/ui/domain/version-select/VersionSelect';

import type { JobItemDetails } from '../api/use-job-item-details';
import { Action as ActionWrapper, CTAWrapper } from '../components/Action';
import type { SaveOptions } from '../components/SaveWithAutoTranslationButton';
import { SaveWithAutoTranslationButton } from '../components/SaveWithAutoTranslationButton';
import { useFormattedVersions } from '../hooks/use-formatted-versions';
import { RegenerateDialog } from './RegenerateDialog';

const VERSION_WRAPPER_CLASS = 'flex w-full min-w-0 flex-auto flex-row items-start gap-s';
const VERSION_SELECT_WRAPPER_CLASS = 'flex w-full min-w-0 flex-auto flex-col items-start gap-xxs';

const getCurrentVersionNumber = (selectedVersion: string | undefined): number =>
    is.nonEmptyStringAndNotWhitespace(selectedVersion) ? Number(selectedVersion) : Number.NaN;

const getNewestVersion = (versions: readonly JobProfileVersion[]): JobProfileVersion | undefined =>
    is.nonEmptyArray(versions)
        ? versions.find((version) => version.version === Math.max(...versions.map((item) => item.version)))
        : undefined;

const MenuItemWithIcon: FunctionComponent<{ readonly children: ReactNode }> = ({ children }) => (
    <span className="flex items-center gap-s">{children}</span>
);

export const EditMenu: FunctionComponent<{
    readonly disabled?: boolean;
    readonly editLabel: string;
    readonly editManuallyLabel: string;
    readonly editWithAiDisabledReason?: string;
    readonly editWithAiLabel: string;
    readonly onEditManually: () => void;
    readonly onEditWithAi: () => void;
}> = ({
    disabled = false,
    editLabel,
    editManuallyLabel,
    editWithAiDisabledReason,
    editWithAiLabel,
    onEditManually,
    onEditWithAi,
}) => (
    <DropdownMenu>
        <DropdownMenuTrigger disabled={disabled}>
            <Button className="px-xl!" disabled={disabled} iconPosition="trailing" variant="tertiary">
                <span>{editLabel}</span>
                <IconChevronDownSmall style={{ height: '16px', width: '16px' }} />
            </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" sideOffset={5}>
            <DropdownMenuItem
                disabled={disabled || editWithAiDisabledReason !== undefined}
                tooltipContent={editWithAiDisabledReason}
                onSelect={onEditWithAi}
            >
                <MenuItemWithIcon>
                    <IconAsterisk size={16} />
                    {editWithAiLabel}
                </MenuItemWithIcon>
            </DropdownMenuItem>
            <DropdownMenuItem disabled={disabled} onSelect={onEditManually}>
                <MenuItemWithIcon>
                    <IconSpacer />
                    {editManuallyLabel}
                </MenuItemWithIcon>
            </DropdownMenuItem>
        </DropdownMenuContent>
    </DropdownMenu>
);

export const Actions: FunctionComponent<{
    readonly isArchived?: boolean;
    readonly isEditing: boolean;
    readonly isRegenerateDialogOpen: boolean;
    readonly item: JobItemDetails;
    readonly onCancel: () => void;
    readonly onNewVersion: () => void;
    readonly onRegenerateDialogOpenChange: (open: boolean) => void;
    readonly onRequestRegenerate: () => void;
    readonly onSave: (options: SaveOptions) => Promise<void>;
    readonly onVersionChange: (value: string) => void;
    readonly permissions: {
        readonly canEdit: boolean;
    };
    readonly selectedVersion: string | undefined;
    readonly versions: readonly JobProfileVersion[];
}> = ({
    isArchived,
    isEditing,
    isRegenerateDialogOpen,
    item,
    onCancel,
    onNewVersion,
    onRegenerateDialogOpenChange,
    onRequestRegenerate,
    onSave,
    onVersionChange,
    permissions: { canEdit },
    selectedVersion,
    versions,
}) => {
    const translate = useTranslate();
    const { formattedVersions, newTmpVersion } = useFormattedVersions(versions);
    const currentVersionNumber = getCurrentVersionNumber(selectedVersion);
    const newerVersionTarget = getNewestVersion(versions);
    const hasNewerVersion =
        !is.nullOrUndefined(newerVersionTarget) &&
        Number.isFinite(currentVersionNumber) &&
        currentVersionNumber < newerVersionTarget.version;

    const editLabel = translate('common.edit');
    const editWithAiLabel = translate('pages.job-architecture-v2.job-profile-edit-with-ai');
    const editManuallyLabel = translate('pages.job-architecture-v2.job-profile-edit-manually');
    const versionPlaceholder = translate('pages.job-architecture-v2.job-profile-version-placeholder');
    const editingActions = (
        <CTAWrapper>
            <Button variant="tertiary" onClick={onCancel}>
                {translate('common.cancel')}
            </Button>
            <SaveWithAutoTranslationButton onSave={onSave} />
        </CTAWrapper>
    );

    const newerVersionBadge =
        !isEditing && hasNewerVersion ? (
            <Badge
                aria-label={translate('pages.job-architecture-v2.action-bar.newer-version-available-aria', {
                    version: newerVersionTarget.version,
                })}
                data-testid="newer-version-available-pill"
                variant="warning"
                onClick={() => {
                    onVersionChange(newerVersionTarget.version.toString());
                }}
            >
                {translate('pages.job-architecture-v2.action-bar.newer-version-available')}
            </Badge>
        ) : null;

    const editButton = (
        <RegenerateDialog
            architectureId={item.architectureId}
            isOpen={isRegenerateDialogOpen}
            itemId={item.id}
            itemName={item.title}
            itemType={item.type}
            parentRoleId={item.type === 'job-level' && !is.null(item.parentId) ? item.parentId : undefined}
            version={selectedVersion ?? item.versionInfo?.version ?? ''}
            onOpenChange={onRegenerateDialogOpenChange}
        >
            {() => (
                // RegenerateDialog anchors its popover here (anchorOnly) — Radix needs a DOM node.
                <span className="inline-flex">
                    <EditMenu
                        disabled={!canEdit || isArchived}
                        editLabel={editLabel}
                        editManuallyLabel={editManuallyLabel}
                        editWithAiLabel={editWithAiLabel}
                        onEditManually={onNewVersion}
                        onEditWithAi={onRequestRegenerate}
                    />
                </span>
            )}
        </RegenerateDialog>
    );

    const defaultActions = <CTAWrapper>{editButton}</CTAWrapper>;

    return (
        <ActionWrapper $surface="integrated" data-testid="job-profile-actions">
            <div className={VERSION_WRAPPER_CLASS}>
                <div className={VERSION_SELECT_WRAPPER_CLASS}>
                    <VersionSelect
                        ariaLabel={versionPlaceholder}
                        contentInset={isEditing ? 'none' : 'table'}
                        currentVersion={
                            isEditing ? newTmpVersion : formattedVersions.find((v) => v.id === selectedVersion)
                        }
                        disabled={formattedVersions.length === 0 || isEditing}
                        placeholder={versionPlaceholder}
                        versions={formattedVersions}
                        onSwitchVersion={(version) => {
                            onVersionChange(version);
                        }}
                    />
                </div>
            </div>
            <div className="flex min-w-0 items-center">{isEditing ? null : newerVersionBadge}</div>
            {isEditing && canEdit ? editingActions : defaultActions}
        </ActionWrapper>
    );
};
